/**
 * Hook: useContractTemplateVersions
 * Descrição: Busca as versões históricas de um template de contrato via RPC server-side
 *
 * RELEASE: v2.0.0 - 2026-05-10
 * - Substitui SELECT direto em contract_template_versions pela RPC
 *   get_contract_template_versions (tenant validado server-side)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ContractTemplateVersion } from '@/types/contracts';

// Logger do módulo
const logger = createLogger('ContractTemplateVersions');

/**
 * Hook para buscar versões históricas de um template de contrato
 * @param templateId - ID do template (undefined = desabilitado)
 */
export function useContractTemplateVersions(templateId: string | undefined) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['contract-template-versions', templateId, tenantId],
    queryFn: async () => {
      if (!templateId) return [];

      logger.debug('Buscando versões do template (RPC)', { templateId });

      const { data, error } = await supabase.rpc('get_contract_template_versions', {
        p_template_id: templateId,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar versões do template', { error });
        throw error;
      }

      logger.info('Versões carregadas', { count: data?.length, templateId });
      return (data || []) as unknown as ContractTemplateVersion[];
    },
    enabled: !!templateId && !!tenantId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}
